import { useEffect, useState } from 'react';
import { Loader2, Plus, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';
import { createSchedule, getSchedulesByClub } from '@/api/schedule/scheduleApi';
import { SCHEDULE_FIELDS } from '@/api/schedule/schedule-fields.config';
import { TournamentStatus, type Club } from '@/types/tournament.model';
import type { Schedule } from '@/types/schedule.model';
import { handleGlobalError } from '@/utils/error-handler';
import { CreateScheduleBlockForm } from '@/features/tournament-wizard/components/create-schedule-block';
import { isEligibleWeeklyGrid } from '@/features/tournament-wizard/utils/scheduleEligibility';

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
/** "Lun", "Mié", "Sáb": the first three letters of the shared weekday label. */
const shortDayLabel = (day: string): string => (dayOfWeekMap[day] ?? day).slice(0, 3);

/** "Mié 19:00, 20:30 · Sáb 10:00": the grid's start times per weekday, Monday first. */
function describeGridRows(grid: Schedule): string {
  return [...(grid.schedule ?? [])]
    .sort((a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek))
    .map(
      (entry) =>
        `${shortDayLabel(entry.dayOfWeek)} ${entry.timeRange
          .map((r) => r.startTime)
          .sort()
          .join(', ')}`,
    )
    .join(' · ');
}

interface WeeklyGridPickerProps {
  club: Club;
  sportUid?: string | null;
  matchDuration: number;
  /** First-round start day. The new-grid editor draws that week, so creating a grid needs it. */
  weekStartDate: Date | null;
  selectedGridId?: string;
  onSelect: (grid: Schedule) => void;
}

/**
 * Picks the weekly grid of a LONG tournament: one the club already has, which makes the tournament a
 * sibling of the ones using it, or a new one drawn over the first week.
 */
export function WeeklyGridPicker({
  club,
  sportUid,
  matchDuration,
  weekStartDate,
  selectedGridId,
  onSelect,
}: WeeklyGridPickerProps) {
  const [grids, setGrids] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getSchedulesByClub(club.id, SCHEDULE_FIELDS.WITH_TOURNAMENTS)
      .then((schedules) => {
        if (!cancelled) {
          setGrids(schedules.filter((s) => isEligibleWeeklyGrid(s, { sportUid, matchDuration })));
        }
      })
      .catch((err) => handleGlobalError(err, 'Error', 'No se pudieron cargar las grillas del club'))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [club.id, sportUid, matchDuration]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900">Grilla semanal de horarios</p>
        {!isCreating && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCreating(true)}
            disabled={!weekStartDate}
            title={weekStartDate ? undefined : 'Elegí primero el día en que arranca la primera fecha'}
          >
            <Plus className="mr-1 h-4 w-4" />
            Crear grilla nueva
          </Button>
        )}
      </div>

      {isCreating && weekStartDate ? (
        <CreateScheduleBlockForm
          club={club}
          matchDuration={matchDuration}
          sportUid={sportUid}
          weekStartDate={weekStartDate}
          initialName="Grilla semanal"
          submitLabel="Crear grilla"
          submittingLabel="Creando..."
          onClose={() => setIsCreating(false)}
          onSubmitRequest={async (request) => {
            try {
              const created = await createSchedule(
                club.id,
                {
                  name: request.name,
                  isWeeklyGrid: true,
                  // The editor draws a concrete week; a grid keeps only each weekday.
                  schedule: request.schedule.map((entry) => ({
                    dayOfWeek: entry.dayOfWeek,
                    timeRange: entry.timeRange,
                  })),
                },
                SCHEDULE_FIELDS.WITH_TOURNAMENTS,
              );
              setGrids((prev) => [...prev, created]);
              onSelect(created);
              toast({ title: 'Grilla creada' });
            } catch (err) {
              handleGlobalError(err, 'Error', 'No se pudo crear la grilla');
              throw err;
            }
          }}
        />
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando grillas...
        </div>
      ) : grids.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Este club no tiene grillas con franjas de {matchDuration} minutos para este deporte. Creá una nueva.
        </p>
      ) : (
        <div className="space-y-2">
          {grids.map((grid) => {
            const users = (grid.tournaments ?? []).filter((t) => t.status !== TournamentStatus.CANCELLED);
            const selected = grid.id === selectedGridId;
            return (
              <button
                key={grid.id}
                type="button"
                onClick={() => onSelect(grid)}
                className={cn(
                  'w-full rounded-lg border p-3 text-left transition-colors hover:bg-gray-50',
                  selected && 'border-blue-500 bg-blue-50',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{grid.name}</span>
                  {selected && <Badge>Elegida</Badge>}
                </div>
                <p className="text-sm text-gray-600">{describeGridRows(grid)}</p>
                {users.length > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" />
                    La usan: {users.map((t) => t.name ?? 'torneo sin nombre').join(', ')}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
